(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const toNumber = (value, fallback = 0) => {
    const parsed = Number.parseFloat(String(value || '').replace('s', '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const isInteractiveTarget = (target) => {
    if (!target) return false;
    return Boolean(
      target.closest('a, button, input, select, textarea, label, [role="button"], [data-no-pause]')
    );
  };

  const setupAnimator = (root) => {
    if (!root || root.dataset.heroAnimatorInit === 'true') return;
    root.dataset.heroAnimatorInit = 'true';

    if (prefersReducedMotion) return;

    const stopOnClick = root.dataset.stopOnClick === 'true';
    if (!stopOnClick) return;

    const smoothDuration = Number(root.dataset.stopDuration || 500);
    let isPaused = false;
    let isBusy = false;
    const toggleButton = root.querySelector('[data-hero-animator-toggle]');

    const getAnimations = () => root.getAnimations({ subtree: true });

    const setToggleUI = (paused) => {
      if (!toggleButton) return;
      toggleButton.setAttribute('aria-label', paused ? 'Toista animaatio' : 'Pysäytä animaatio');
      const label = toggleButton.querySelector('.visually-hidden');
      if (label) label.textContent = paused ? 'Toista animaatio' : 'Pysäytä animaatio';
      toggleButton.dataset.state = paused ? 'paused' : 'playing';
      if (!paused) {
        toggleButton.classList.remove('hero-animator__toggle--flash');
      }
    };

    const flashToggle = () => {
      if (!toggleButton) return;
      toggleButton.classList.add('hero-animator__toggle--flash');
      window.setTimeout(() => {
        if (toggleButton.dataset.state !== 'paused') {
          toggleButton.classList.remove('hero-animator__toggle--flash');
        }
      }, 1500);
    };

    const smoothPause = (useSmooth = true) => {
      if (isBusy) return;
      root.classList.add('is-paused');
      const animations = getAnimations();
      if (!animations.length) {
        isPaused = true;
        setToggleUI(true);
        return;
      }

      const isActiveAnimation = (anim) => {
        if (!anim) return false;
        const timing = anim.effect?.getComputedTiming?.();
        if (timing?.phase) return timing.phase === 'active';
        return anim.playState === 'running';
      };

      const isBeforePhase = (anim) => {
        const timing = anim?.effect?.getComputedTiming?.();
        return timing?.phase === 'before';
      };

      const activeAnimations = animations.filter(isActiveAnimation);
      const pendingAnimations = animations.filter((anim) => isBeforePhase(anim));
      pendingAnimations.forEach((anim) => anim.pause());

      if (!useSmooth || !Number.isFinite(smoothDuration) || smoothDuration <= 0 || !activeAnimations.length) {
        activeAnimations.forEach((anim) => anim.pause());
        isPaused = true;
        setToggleUI(true);
        return;
      }

      isBusy = true;
      activeAnimations.forEach((anim) => {
        anim.playbackRate = 1;
      });

      const start = performance.now();
      const easeOut = (value) => 1 - Math.pow(1 - value, 2);

      const tick = (now) => {
        const progress = Math.min((now - start) / smoothDuration, 1);
        const eased = easeOut(progress);
        const rate = Math.max(0, 1 - eased);

        activeAnimations.forEach((anim) => {
          anim.playbackRate = rate;
        });

        if (progress < 1) {
          requestAnimationFrame(tick);
          return;
        }

        activeAnimations.forEach((anim) => {
          anim.playbackRate = 0;
          anim.pause();
        });
        isPaused = true;
        isBusy = false;
        setToggleUI(true);
      };

      requestAnimationFrame(tick);
    };

    const resume = () => {
      const animations = getAnimations();
      animations.forEach((anim) => {
        if (anim.playState === 'paused') {
          anim.playbackRate = 1;
          anim.play();
        }
      });
      root.classList.remove('is-paused');
      isPaused = false;
      isBusy = false;
      setToggleUI(false);
    };

    let lastTouchToggleAt = 0;
    const shouldIgnoreClick = () => Date.now() - lastTouchToggleAt < 600;

    const handleToggle = (event) => {
      if (isInteractiveTarget(event.target)) return;
      if (isBusy) return;

      if (isPaused) {
        resume();
      } else {
        smoothPause(false);
      }
      flashToggle();
    };

    root.addEventListener('pointerup', (event) => {
      if (event.pointerType !== 'touch') return;
      lastTouchToggleAt = Date.now();
      handleToggle(event);
    });

    root.addEventListener('click', (event) => {
      if (shouldIgnoreClick()) return;
      handleToggle(event);
    });

    if (toggleButton) {
      setToggleUI(isPaused);
      toggleButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isBusy) return;
        if (isPaused) {
          resume();
        } else {
          smoothPause(false);
        }
        flashToggle();
      });
    }
  };

  const setupCinematicVariation = (root) => {
    if (!root || root.dataset.cinematicVariationInit === 'true') return;
    root.dataset.cinematicVariationInit = 'true';

    const baseStyle = String(root.dataset.baseStyle || '');
    if (baseStyle === 'none') return;

    const layers = Array.from(root.querySelectorAll('.hero-animator__base.is-multi .hero-animator__layer'))
      .filter((layer) => !layer.classList.contains('ha-layer--cinematic-static'));
    if (layers.length < 2) return;

    const baseCount = Math.max(1, Number.parseInt(root.dataset.baseCount || String(layers.length), 10) || layers.length);
    const baseDefaultDurationS =
      toNumber(root.dataset.baseDefaultDuration, 0) ||
      toNumber(getComputedStyle(root).getPropertyValue('--ha-base-duration-default'), 0) ||
      toNumber(getComputedStyle(root).getPropertyValue('--ha-base-duration'), 0);

    if (baseDefaultDurationS <= 0) return;

    const defaultSlotMs = (baseDefaultDurationS * 1000) / baseCount;
    const visibleDurationsMs = layers.map((layer) => {
      const custom = toNumber(layer.dataset.visibleSeconds, 0);
      return custom > 0 ? custom * 1000 : defaultSlotMs;
    });

    const inMs = toNumber(getComputedStyle(root).getPropertyValue('--ha-base-in-duration'), 0);
    const outMs = toNumber(getComputedStyle(root).getPropertyValue('--ha-base-out-duration'), 0);
    const transitionMs = baseStyle === 'none' ? 0 : Math.max(1, Math.round(Math.max(inMs, outMs, 0.5) * 1000));

    const totalVisibleMs = visibleDurationsMs.reduce((sum, value) => sum + value, 0);
    const totalCycleMs = Math.max(baseDefaultDurationS * 1000, totalVisibleMs + (transitionMs * layers.length));
    const shouldLoop = root.dataset.loop === 'true';

    root.style.setProperty('--ha-base-duration', `${(totalCycleMs / 1000).toFixed(3)}s`);

    const staticLayers = root.querySelectorAll('.hero-animator__base.is-multi .ha-layer--cinematic-static');
    staticLayers.forEach((layer) => {
      layer.style.setProperty('display', 'none', 'important');
      layer.style.setProperty('animation', 'none', 'important');
    });

    root.querySelectorAll('style[data-ha-dynamic]').forEach((styleEl) => styleEl.remove());
    const dynamicStyleEl = document.createElement('style');
    dynamicStyleEl.setAttribute('data-ha-dynamic', 'true');

    const getVar = (layer, name, fallback) => {
      const inline = layer.style.getPropertyValue(name).trim();
      if (inline) return inline;
      const computed = getComputedStyle(layer).getPropertyValue(name).trim();
      return computed || fallback;
    };

    const escapeName = (value) => String(value).replace(/[^a-zA-Z0-9_-]/g, '_');
    const transformVarsFromStyle = (layer) => {
      if (baseStyle.includes('cinematic')) {
        const fromX = getVar(layer, '--ha-pan-x-from', '-3%');
        const fromY = getVar(layer, '--ha-pan-y-from', '0%');
        const toX = getVar(layer, '--ha-pan-x-to', '3%');
        const toY = getVar(layer, '--ha-pan-y-to', '0%');
        const fromScale = getVar(layer, '--ha-pan-scale-from', '1.06');
        const toScale = getVar(layer, '--ha-pan-scale-to', '1.16');
        return {
          fromTransform: `scale(${fromScale}) translate(${fromX}, ${fromY})`,
          toTransform: `scale(${toScale}) translate(${toX}, ${toY})`
        };
      }

      if (baseStyle === 'kenburns-left') {
        return {
          fromTransform: 'scale(1.08) translateX(-4%)',
          toTransform: 'scale(1.16) translateX(4%)'
        };
      }
      if (baseStyle === 'kenburns-right') {
        return {
          fromTransform: 'scale(1.08) translateX(4%)',
          toTransform: 'scale(1.16) translateX(-4%)'
        };
      }
      if (baseStyle === 'slow-zoom') {
        return {
          fromTransform: 'scale(1.05)',
          toTransform: 'scale(1.18)'
        };
      }
      if (baseStyle === 'drift') {
        return {
          fromTransform: 'scale(1.08) translateY(-2%)',
          toTransform: 'scale(1.12) translateY(2%)'
        };
      }
      return {
        fromTransform: 'none',
        toTransform: 'none'
      };
    };
    const baseKeyframeName = `ha_cinematic_slot_${escapeName(root.id || 'root')}_${Date.now()}`;
    let cursorMs = 0;
    let keyframesCss = '';

    layers.forEach((layer, index) => {
      const visibleMs = visibleDurationsMs[index];
      const phaseShiftMs = shouldLoop ? transitionMs : 0;
      const slotStartMs = cursorMs - phaseShiftMs;
      const fadeInEndMs = slotStartMs + transitionMs;
      const holdEndMs = fadeInEndMs + visibleMs;
      const fadeOutEndMs = holdEndMs + transitionMs;
      cursorMs += (visibleMs + transitionMs);

      const startPct = (slotStartMs / totalCycleMs) * 100;
      const fadeInEndPct = (fadeInEndMs / totalCycleMs) * 100;
      const holdEndPct = (holdEndMs / totalCycleMs) * 100;
      const fadeOutEndPct = (fadeOutEndMs / totalCycleMs) * 100;

      const { fromTransform, toTransform } = transformVarsFromStyle(layer);
      const keyframeName = `${baseKeyframeName}_${index}`;
      const fadeInEnd = Math.min(100, Math.max(0, fadeInEndPct));
      const holdEnd = Math.min(100, Math.max(0, holdEndPct));
      const fadeOutEnd = Math.min(100, Math.max(0, fadeOutEndPct));

      const wrapsAtStart = shouldLoop && startPct < 0;
      if (wrapsAtStart) {
        const wrapStart = Math.min(100, Math.max(0, 100 + startPct));
        keyframesCss += `
@keyframes ${keyframeName} {
  0% { opacity: 1; transform: ${fromTransform}; }
  ${holdEnd}% { opacity: 1; transform: ${toTransform}; }
  ${fadeOutEnd}% { opacity: 0; transform: ${toTransform}; }
  ${wrapStart}% { opacity: 0; transform: ${fromTransform}; }
  100% { opacity: 1; transform: ${fromTransform}; }
}
`;
      } else {
        const start = Math.min(100, Math.max(0, startPct));
        keyframesCss += `
@keyframes ${keyframeName} {
  0%, ${start}% { opacity: 0; transform: ${fromTransform}; }
  ${fadeInEnd}% { opacity: 1; transform: ${fromTransform}; }
  ${holdEnd}% { opacity: 1; transform: ${toTransform}; }
  ${fadeOutEnd}%, 100% { opacity: 0; transform: ${toTransform}; }
}
`;
      }
      const animationValue = `${keyframeName} ${totalCycleMs}ms linear 0ms ${shouldLoop ? 'infinite' : '1'} both`;
      layer.style.setProperty('animation', animationValue, 'important');
      layer.style.setProperty('animation-name', keyframeName, 'important');
      layer.style.setProperty('animation-duration', `${totalCycleMs}ms`, 'important');
      layer.style.setProperty('animation-delay', '0ms', 'important');
      layer.style.setProperty('animation-timing-function', 'linear', 'important');
      layer.style.setProperty('animation-iteration-count', shouldLoop ? 'infinite' : '1', 'important');
      layer.style.setProperty('animation-fill-mode', 'both', 'important');
    });

    dynamicStyleEl.textContent = keyframesCss;
    root.appendChild(dynamicStyleEl);
  };

  const setupLetterAnimation = (root) => {
    if (!root) return;

    root.querySelectorAll('.hero-animator__text-inner').forEach((textInner) => {
      if (textInner.dataset.letterAnimationInit === 'true') return;
      textInner.dataset.letterAnimationInit = 'true';

      const hasLetterIn = textInner.classList.contains('ha-text-in--letters-in');
      const hasLetterOut = textInner.classList.contains('ha-text-out--letters-out-reverse');
      if (!hasLetterIn && !hasLetterOut) return;

      const textNodes = [];
      const collectTextNodes = (node) => {
        node.childNodes.forEach((childNode) => {
          if (childNode.nodeType === Node.TEXT_NODE) {
            if (childNode.nodeValue && childNode.nodeValue.length) {
              textNodes.push(childNode);
            }
            return;
          }
          if (childNode.nodeType !== Node.ELEMENT_NODE) return;
          const tagName = childNode.tagName?.toLowerCase?.();
          if (tagName === 'script' || tagName === 'style') return;
          collectTextNodes(childNode);
        });
      };

      collectTextNodes(textInner);

      let letterCount = 0;
      textNodes.forEach((textNode) => {
        const fragment = document.createDocumentFragment();
        let currentWord = null;
        Array.from(textNode.nodeValue || '').forEach((character) => {
          if (!character.trim()) {
            currentWord = null;
            fragment.appendChild(document.createTextNode(character));
            return;
          }

          if (!currentWord) {
            currentWord = document.createElement('span');
            currentWord.className = 'ha-word';
            fragment.appendChild(currentWord);
          }

          const letter = document.createElement('span');
          letter.className = 'ha-letter';
          letter.textContent = character;
          letter.style.setProperty('--ha-letter-index', `${letterCount}`);
          currentWord.appendChild(letter);
          letterCount += 1;
        });
        textNode.replaceWith(fragment);
      });

      if (!letterCount) return;

      const letters = Array.from(textInner.querySelectorAll('.ha-letter'));
      letters.forEach((letter, index) => {
        letter.style.setProperty('--ha-letter-index-reverse', `${letterCount - index - 1}`);
      });
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-hero-animator]').forEach((root) => {
      setupAnimator(root);
      setupCinematicVariation(root);
      setupLetterAnimation(root);
      window.requestAnimationFrame(() => {
        root.dataset.haReady = 'true';
        const baseCount = Math.max(1, Number.parseInt(root.dataset.baseCount || '1', 10) || 1);
        const shouldLoop = root.dataset.loop === 'true';
        if (!shouldLoop || baseCount <= 1) {
          root.dataset.haInitial = 'false';
          return;
        }
        const durationS = toNumber(getComputedStyle(root).getPropertyValue('--ha-base-duration'), 0);
        const durationMs = Math.max(600, Math.round(durationS * 1000));
        window.setTimeout(() => {
          root.dataset.haInitial = 'false';
        }, durationMs);
      });
    });
  };

  initAll();

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
